import { baseApi } from "@/core/api/baseApi";
import { publicApi } from "@/core/api/publicApi";
import type { UserLoginContext } from "@/lib/authScope";

interface ILoginRequest {
  merchantId: string;
  username: string;
  password: string;
}

interface ILoginResponse {
  message: string;
  username: string;
  emailId: string;
  phoneNumber: string;
}

interface ISendOtpRequest {
  userName: string;
  merchantId: string;
  otpChannel: "EMAIL" | "SMS";
}

interface ISendOtpResponse {
  message: string;
  username: string;
  jwtToken: string;
  emailId: string;
  phoneNumber: string;
}

interface IVerifyOtpRequest {
  userName: string;
  merchantId: string;
  otp: string;
  otpChannel: "EMAIL" | "SMS";
}

interface IVerifyOtpResponse {
  message: string;
  username: string;
  jwtToken: string;
  /**
   * The same context `GET /auth/me` returns, minted with the token so the
   * first authenticated render already knows the menu, the seat chain and
   * whether the password must be changed.
   */
  context?: UserLoginContext;
}

interface IResetPasswordRequest {
  userName: string;
  merchantId: string;
  /** Required for non-admins changing their own password. */
  oldPassword?: string;
  newPassword: string;
}

const otpQuery = (path: string, request: ISendOtpRequest | IVerifyOtpRequest, extra = "") => ({
  url:
    `/auth/otp/${path}?merchantId=${encodeURIComponent(request.merchantId)}` +
    `&username=${encodeURIComponent(request.userName)}` +
    `&otpChannel=${request.otpChannel}${extra}`,
  method: "POST",
});

/**
 * Sign-in endpoints run on the ANONYMOUS client. Sending a stale bearer token
 * with a login attempt lets the server answer for the old session instead of
 * the credentials being presented.
 */
export const publicAuthApi = publicApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (build) => ({
    login: build.mutation<ILoginResponse, ILoginRequest>({
      query: (body: ILoginRequest) => ({ url: "/auth/login", method: "POST", body }),
    }),
    sendOtp: build.mutation<ISendOtpResponse, ISendOtpRequest>({
      query: (request: ISendOtpRequest) => otpQuery("send", request),
    }),
    verifyOtp: build.mutation<IVerifyOtpResponse, IVerifyOtpRequest>({
      query: (request: IVerifyOtpRequest) =>
        otpQuery("validate", request, `&otp=${encodeURIComponent(request.otp)}`),
    }),
  }),
});

export const authApi = baseApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (build) => ({
    /**
     * The authorisation context, re-read from the user row on every call.
     * Every other endpoint trusts the JWT alone, so this is also the only
     * request that notices an account being switched off mid-session.
     */
    getMe: build.query<UserLoginContext, void>({
      query: () => ({ url: "/auth/me", method: "GET" }),
      providesTags: ["Session"],
    }),
    /** Blocklists this token's `jti` server-side — clearing storage does not. */
    logout: build.mutation<unknown, void>({
      query: () => ({ url: "/auth/logout", method: "POST" }),
    }),
    resetPassword: build.mutation<unknown, IResetPasswordRequest>({
      query: (body: IResetPasswordRequest) => ({
        url: "/auth/reset-password",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Session"],
    }),
  }),
});

export const { useLoginMutation, useSendOtpMutation, useVerifyOtpMutation } = publicAuthApi;
export const { useGetMeQuery, useLogoutMutation, useResetPasswordMutation } = authApi;
export type { IVerifyOtpResponse, IResetPasswordRequest };
